/**
 * MetricCard.styles — Tokens de color y tono de la tarjeta KPI compartida.
 *
 * QUÉ HACE: concentra los mapas de clases Tailwind por color/tono que usa
 *   `MetricCard` (badge de estado, fondo/icono, tendencia, sparkline y barras
 *   semanales) para no repetir estilos en el JSX.
 * QUÉ INVOCA: iconos de lucide-react.
 * QUÉ EXPORTA: badgeToneVariants, badgeToneClasses, badgeToneTextClasses,
 *   colorStyles, trendStyles, sparklineColorStyles, weeklyBarFillStyles.
 * QUÉ NO DEBE HACER: no renderiza ni calcula datos.
 */

import type { LucideIcon } from "lucide-react";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { BadgeVariant } from "@/components/ui/Badge";
import type {
  MetricBadgeTone,
  MetricColor,
  MetricTrendDirection,
} from "./MetricCard.types";

/** Mapeo del tono semántico a la variante base del `Badge`. */
export const badgeToneVariants: Record<MetricBadgeTone, BadgeVariant> = {
  success: "success",
  warning: "warning",
  critical: "error",
  info: "info",
};

/** Tokens de color exactos por tono (se imponen a la variante del `Badge`). */
export const badgeToneClasses: Record<MetricBadgeTone, string> = {
  success:
    "!bg-emerald-50 !text-emerald-700 !border-emerald-200 dark:!bg-emerald-900/30 dark:!text-emerald-400 dark:!border-emerald-800",
  warning:
    "!bg-amber-50 !text-amber-700 !border-amber-200 dark:!bg-amber-900/30 dark:!text-amber-400 dark:!border-amber-800",
  critical:
    "!bg-rose-50 !text-rose-700 !border-rose-200 dark:!bg-rose-900/30 dark:!text-rose-400 dark:!border-rose-800",
  info: "!bg-sky-50 !text-sky-700 !border-sky-200 dark:!bg-sky-900/30 dark:!text-sky-400 dark:!border-sky-800",
};

/** Tokens de color de texto por tono (badge como texto en móvil). */
export const badgeToneTextClasses: Record<MetricBadgeTone, string> = {
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  critical: "text-rose-600 dark:text-rose-400",
  info: "text-sky-600 dark:text-sky-400",
};

export const colorStyles: Record<MetricColor, { bg: string; text: string }> = {
  blue: {
    bg: "bg-blue-50 dark:bg-blue-900/30",
    text: "text-blue-600 dark:text-blue-400",
  },
  green: {
    bg: "bg-green-50 dark:bg-green-900/30",
    text: "text-green-600 dark:text-green-400",
  },
  amber: {
    bg: "bg-amber-50 dark:bg-amber-900/30",
    text: "text-amber-600 dark:text-amber-400",
  },
  purple: {
    bg: "bg-purple-50 dark:bg-purple-900/30",
    text: "text-purple-600 dark:text-purple-400",
  },
  orange: {
    bg: "bg-orange-50 dark:bg-orange-900/30",
    text: "text-orange-600 dark:text-orange-400",
  },
  red: {
    bg: "bg-red-50 dark:bg-red-900/30",
    text: "text-red-600 dark:text-red-400",
  },
};

export const trendStyles: Record<
  MetricTrendDirection,
  { text: string; Icon: LucideIcon }
> = {
  up: { text: "text-green-600 dark:text-green-400", Icon: TrendingUp },
  down: { text: "text-red-500 dark:text-red-400", Icon: TrendingDown },
  neutral: { text: "text-gray-500 dark:text-slate-400", Icon: Minus },
};

/** Color del trazo/relleno de la mini-gráfica de área (hereda `currentColor`). */
export const sparklineColorStyles: Record<MetricColor, string> = {
  blue: "text-blue-600 dark:text-blue-400",
  green: "text-green-600 dark:text-green-400",
  amber: "text-amber-600 dark:text-amber-400",
  purple: "text-purple-600 dark:text-purple-400",
  orange: "text-orange-600 dark:text-orange-400",
  red: "text-red-600 dark:text-red-400",
};

/** Relleno de las barras semanales, alineado al color de la tarjeta. */
export const weeklyBarFillStyles: Record<MetricColor, string> = {
  blue: "bg-blue-600 dark:bg-blue-500",
  green: "bg-green-600 dark:bg-green-500",
  amber: "bg-amber-500",
  purple: "bg-purple-600 dark:bg-purple-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
};
